import React, { useState, useRef, useCallback, useEffect } from "react";
import { API_URL } from "../config";
import "./Studio.css";

function getToken() {
  return localStorage.getItem("token") || "";
}

function ImageModal({ src, label, onClose }) {
  if (!src) return null;
  return (
    <div className="st-modal-overlay" onClick={onClose}>
      <div className="st-modal" onClick={(e) => e.stopPropagation()}>
        <button className="st-modal__close" onClick={onClose}>&times;</button>
        <img src={src} alt={label} className="st-modal__image" />
        <span className="st-modal__label">{label}</span>
      </div>
    </div>
  );
}

const MODELS = [
  { id: "kling25", label: "Kling 2.5 (pruebas)" },
  { id: "kling3", label: "Kling 3 Omni (produccion)" },
  { id: "seedance2", label: "Seedance 2.0" },
];

const RATIOS = ["16:9", "9:16", "1:1"];
const DURATIONS = ["5s", "10s"];

export default function StudioVideo() {
  /* --- Shot images (from Crear Imagen de Toma) --- */
  const [shotImages, setShotImages] = useState([]); // TODO: load from saved shots
  const [selectedShot, setSelectedShot] = useState("");

  /* --- Character sheets for reinforcement --- */
  const [characters, setCharacters] = useState([]);
  const [selectedSheets, setSelectedSheets] = useState([]);

  /* --- Face refs for reinforcement --- */
  const [selectedFaces, setSelectedFaces] = useState([]);

  /* --- Prompts --- */
  const [idea, setIdea] = useState("");
  const [prompter, setPrompter] = useState("");

  /* --- Settings --- */
  const [model, setModel] = useState("kling25");
  const [ratio, setRatio] = useState("16:9");
  const [duration, setDuration] = useState("5s");
  const [seed, setSeed] = useState("");

  /* --- Generation --- */
  const [status, setStatus] = useState("idle");
  const [resultVideo, setResultVideo] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [modalSrc, setModalSrc] = useState(null);

  /* --- Reel refs --- */
  const shotsReelRef = useRef(null);
  const sheetsReelRef = useRef(null);
  const facesReelRef = useRef(null);
  const scrollReel = (ref, dir) => {
    if (ref.current) ref.current.scrollBy({ left: dir * 200, behavior: "smooth" });
  };

  /* --- Load characters with sheets and faces --- */
  useEffect(() => {
    const headers = { Authorization: `Bearer ${getToken()}` };
    fetch(`${API_URL}/characters`, { headers })
      .then((r) => r.json())
      .then(async (d) => {
        const chars = d.characters || [];
        const withDetails = await Promise.all(
          chars.map(async (c) => {
            const cid = c.id || c;
            try {
              const r2 = await fetch(`${API_URL}/characters/${cid}`, { headers });
              const det = await r2.json();
              return {
                id: cid,
                displayName: det.display_name || cid,
                faceUrl: det?.face?.reference?.[0]?.url || null,
                sheets: (det.sheets || []).filter((s) => s.url),
              };
            } catch { return { id: cid, displayName: cid, faceUrl: null, sheets: [] }; }
          })
        );
        setCharacters(withDetails);
      })
      .catch(() => {});
  }, []);

  const toggleSheet = (key) => setSelectedSheets((p) => p.includes(key) ? p.filter((k) => k !== key) : [...p, key]);
  const toggleFace = (id) => setSelectedFaces((p) => p.includes(id) ? p.filter((k) => k !== id) : [...p, id]);

  const pollRef = useRef(false);
  const pollJob = async (jobId) => {
    pollRef.current = true;
    for (let i = 0; i < 180; i++) {  // 15 min for video
      if (!pollRef.current) return;
      await new Promise((r) => setTimeout(r, 5000));
      try {
        const res = await fetch(`${API_URL}/studio/video/${encodeURIComponent(jobId)}/status`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        const data = await res.json();
        if (data.status === "completed") {
          const rr = await fetch(`${API_URL}/studio/video/${encodeURIComponent(jobId)}/result`, {
            headers: { Authorization: `Bearer ${getToken()}` },
          });
          const rd = await rr.json();
          if (rd.video_url) setResultVideo(rd.video_url);
          setStatus("done");
          pollRef.current = false;
          return;
        }
        if (data.status === "error") {
          setErrorMsg(data.error || "Error al generar");
          setStatus("error");
          pollRef.current = false;
          return;
        }
      } catch (err) { console.error("Poll error:", err); }
    }
    setErrorMsg("Tiempo agotado"); setStatus("error"); pollRef.current = false;
  };

  const getSheetUrls = () => selectedSheets.map((key) => {
    const sheet = allSheets.find((s) => s.key === key);
    return sheet?.url || "";
  }).filter(Boolean);

  const getFaceUrls = () => selectedFaces.map((id) => {
    const face = allFaces.find((f) => f.id === id);
    return face?.url || "";
  }).filter(Boolean);

  const canGenerate = selectedShot && status !== "generating";

  const handleGenerate = async () => {
    setStatus("generating");
    setResultVideo(null);
    setErrorMsg("");
    try {
      const body = {
        shot_url: selectedShot,
        sheet_urls: getSheetUrls(),
        face_urls: getFaceUrls(),
        idea, prompter, model, ratio, duration, seed,
      };
      const res = await fetch(`${API_URL}/studio/video/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${res.status}`);
      }
      const data = await res.json();
      pollJob(data.job_id);
    } catch (err) {
      setErrorMsg(err.message || "Error al generar");
      setStatus("error");
    }
  };

  // Flatten sheets for reel
  const allSheets = characters.flatMap((c) =>
    c.sheets.map((s) => ({
      key: `${c.id}/${s.name}`,
      url: s.url,
      label: `${c.displayName} — ${s.name?.replace(".png", "").replace(/_/g, " ")}`,
    }))
  );

  // Faces for reinforcement reel
  const allFaces = characters.filter((c) => c.faceUrl).map((c) => ({
    id: c.id,
    url: c.faceUrl,
    label: c.displayName,
  }));

  return (
    <div className="studio-page">
      {/* LEFT */}
      <div className="st-panel st-panel--left">
        <div className="st-header">
          <h2 className="st-panel__title">Crear Video-Toma</h2>
          <button
            className={`st-btn-primary ${status === "generating" ? "st-btn-primary--loading" : ""}`}
            disabled={!canGenerate}
            onClick={handleGenerate}
          >
            {status === "generating" ? "Generando..." : "Crear"}
          </button>
        </div>

        {/* Shot images reel */}
        <section className="st-section">
          <span className="st-section__title">Imagenes de toma</span>
          <div className="st-reel">
            <button className="st-reel__btn" onClick={() => scrollReel(shotsReelRef, -1)}>{"\u25C0"}</button>
            <div className="st-reel__frames" ref={shotsReelRef}>
              {shotImages.length > 0 ? (
                shotImages.map((s) => (
                  <div
                    key={s.url || s.name}
                    className={`st-reel-item st-reel-item--sheet ${selectedShot === s.url ? "st-reel-item--selected" : ""}`}
                    onClick={() => setSelectedShot(s.url)}
                  >
                    <img src={s.url} alt={s.name} />
                    <span className="st-reel-item__name">{s.name}</span>
                  </div>
                ))
              ) : (
                <span className="st-reel-empty">Crea imagenes de toma en Crear Imagen de Toma</span>
              )}
            </div>
            <button className="st-reel__btn" onClick={() => scrollReel(shotsReelRef, 1)}>{"\u25B6"}</button>
          </div>
        </section>

        {/* Character sheets for reinforcement */}
        <section className="st-section">
          <span className="st-section__title">Hojas de personaje (refuerzo consistencia)</span>
          <div className="st-reel st-reel--tall">
            <button className="st-reel__btn" onClick={() => scrollReel(sheetsReelRef, -1)}>{"\u25C0"}</button>
            <div className="st-reel__frames" ref={sheetsReelRef}>
              {allSheets.map((s) => (
                <div
                  key={s.key}
                  className={`st-reel-item st-reel-item--sheet ${selectedSheets.includes(s.key) ? "st-reel-item--selected" : ""}`}
                  onClick={() => toggleSheet(s.key)}
                >
                  <img src={s.url} alt={s.label} />
                  <span className="st-reel-item__name">{s.label}</span>
                </div>
              ))}
              {allSheets.length === 0 && <span className="st-reel-empty">Sin hojas de personaje</span>}
            </div>
            <button className="st-reel__btn" onClick={() => scrollReel(sheetsReelRef, 1)}>{"\u25B6"}</button>
          </div>
        </section>

        {/* Face refs for reinforcement */}
        <section className="st-section">
          <span className="st-section__title">Caras base (refuerzo consistencia)</span>
          <div className="st-reel">
            <button className="st-reel__btn" onClick={() => scrollReel(facesReelRef, -1)}>{"\u25C0"}</button>
            <div className="st-reel__frames" ref={facesReelRef}>
              {allFaces.map((f) => (
                <div
                  key={f.id}
                  className={`st-reel-item ${selectedFaces.includes(f.id) ? "st-reel-item--selected" : ""}`}
                  onClick={() => toggleFace(f.id)}
                >
                  <img src={f.url} alt={f.label} />
                  <span className="st-reel-item__name">{f.label}</span>
                </div>
              ))}
              {allFaces.length === 0 && <span className="st-reel-empty">Sin personajes con cara</span>}
            </div>
            <button className="st-reel__btn" onClick={() => scrollReel(facesReelRef, 1)}>{"\u25B6"}</button>
          </div>
        </section>

        {/* Prompts — only movement/action */}
        <section className="st-section">
          <span className="st-section__title">Idea (solo movimiento / accion)</span>
          <textarea
            className="st-textarea"
            placeholder="Describe SOLO el movimiento... (ej: la camara avanza lentamente, el personaje gira la cabeza)"
            value={idea}
            onChange={(e) => setIdea(e.target.value)}
          />
        </section>
        <section className="st-section">
          <span className="st-section__title">Prompter</span>
          <textarea className="st-textarea" placeholder="Prompt tecnico de movimiento..." value={prompter} onChange={(e) => setPrompter(e.target.value)} />
        </section>

        {/* Bottom bar */}
        <div className="st-bottom-bar">
          <div className="st-select-group">
            <span className="st-select-group__label">Motor</span>
            <select className="st-select" value={model} onChange={(e) => setModel(e.target.value)}>
              {MODELS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>
          <div className="st-select-group">
            <span className="st-select-group__label">Ratio</span>
            <select className="st-select" value={ratio} onChange={(e) => setRatio(e.target.value)}>
              {RATIOS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="st-select-group">
            <span className="st-select-group__label">Duracion</span>
            <select className="st-select" value={duration} onChange={(e) => setDuration(e.target.value)}>
              {DURATIONS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="st-select-group">
            <span className="st-select-group__label">Semilla</span>
            <input className="st-seed-input" placeholder="random" value={seed} onChange={(e) => setSeed(e.target.value)} />
          </div>
          <span className="st-credits">Creditos est.: --</span>
        </div>

        {errorMsg && <div className="st-error">{errorMsg}</div>}
      </div>

      {/* RIGHT — Video result */}
      <div className="st-panel st-panel--right">
        <h2 className="st-panel__title">Resultado</h2>
        <div className="st-result">
          <div className={`st-result__image ${resultVideo ? "st-result__image--loaded" : ""}`}>
            {resultVideo ? (
              <video src={resultVideo} controls autoPlay loop />
            ) : (
              <div className="st-results-empty">
                {status === "generating" ? <span className="st-spinner" /> : <p>Selecciona imagen de toma y pulsa Crear</p>}
              </div>
            )}
          </div>
          {resultVideo && (
            <div className="st-result__footer">
              <span className="st-result__label">Video-Toma</span>
              <div className="st-result__actions">
                <button className="st-btn-secondary">Guardar</button>
                <button className="st-btn-download">&#x2B73;</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {modalSrc && <ImageModal src={modalSrc} label="Referencia" onClose={() => setModalSrc(null)} />}
    </div>
  );
}
